import { useTranslation } from "react-i18next";
import { CompanyLogo } from "../../components/CompanyLogo";
import { Card, ErrorBox, FreshnessStamp, Skeleton } from "../../components/ui";
import { usePayrollSummary } from "../../lib/api";
import { fmtValue } from "../../lib/format";
import { SectionCard } from "./shared";

/** Submitted payroll by company and period (WP-23.1).
 *
 * Read-only on purpose. Payroll *configuration* — the SYSCOHADA accounts, the INSS/INPP/ONEM/IRPP components and
 * their rates — is WP-16 and carries indicative dummy values until the customer's payroll adviser signs them off, so this
 * reports what has already been run and links to the ERPNext forms rather than offering to run anything. */
export default function Payroll({ company }: { company: string }) {
  const { t, i18n } = useTranslation();
  const lng = i18n.language;
  const q = usePayrollSummary(company || undefined);

  if (q.isError) return <div className="mt-6"><ErrorBox error={q.error} onRetry={() => void q.refetch()} /></div>;
  if (!q.data) return <div className="mt-6"><Skeleton className="h-64" /></div>;
  const d = q.data;

  if (!d.permitted) {
    return <Card className="mt-4 p-6 text-center text-sm text-brand-grey" data-hr-payroll>{t("hr.payrollNotPermitted")}</Card>;
  }

  return (
    <div className="mt-4 space-y-4" data-hr-payroll>
      <div className="flex justify-end"><FreshnessStamp lastUpdated={d.as_of} /></div>
      <SectionCard title={t("hr.payroll")} aside={t("hr.payrollHint")}>
        {d.runs.length === 0 ? <div className="p-6 text-center text-sm text-brand-grey">{t("hr.noPayroll")}</div> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#F6F8FA] text-left text-[11px] uppercase tracking-wide text-brand-grey">
                <tr>
                  <th className="px-4 py-2">{t("exec.period")}</th>
                  <th className="px-4 py-2">{t("exec.division")}</th>
                  <th className="px-2 py-2 text-right">{t("hr.slips")}</th>
                  <th className="px-2 py-2 text-right">{t("hr.gross")}</th>
                  <th className="px-2 py-2 text-right">{t("hr.deductions")}</th>
                  <th className="px-2 py-2 text-right">{t("hr.net")}</th>
                  <th className="px-2 py-2 text-right">{t("hr.employer")}</th>
                  <th className="px-4 py-2 text-right">{t("hr.totalCost")}</th>
                </tr>
              </thead>
              <tbody>
                {d.runs.map((r) => (
                  <tr key={`${r.company}-${r.period}`} className="border-t border-brand-grey-light">
                    <td className="px-4 py-2">{r.period}</td>
                    <td className="px-4 py-2"><CompanyLogo company={r.company} size={14} showName className="text-xs" /></td>
                    <td className="px-2 py-2 text-right">{r.slips}</td>
                    <td className="px-2 py-2 text-right">{fmtValue(r.gross, "CDF", lng)}</td>
                    <td className="px-2 py-2 text-right text-brand-grey">{fmtValue(r.deductions, "CDF", lng)}</td>
                    <td className="px-2 py-2 text-right font-semibold">{fmtValue(r.net, "CDF", lng)}</td>
                    <td className="px-2 py-2 text-right text-brand-grey">{fmtValue(r.employer_contribution, "CDF", lng)}</td>
                    <td className="px-4 py-2 text-right font-semibold">{fmtValue(r.total_cost, "CDF", lng)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div className="border-t border-brand-grey-light p-4 text-xs text-brand-grey">
          <div className="mb-1 text-[11px] uppercase tracking-wide">{t("hr.components")}</div>
          <div className="flex flex-wrap gap-2">
            {d.components.map((c) => (
              <span key={c.name} className="rounded bg-[#F6F8FA] px-2 py-1" title={c.description ?? ""}>
                {c.name} <span className="text-brand-grey">({c.type}{c.formula ? ` · ${c.formula}` : ""})</span>
              </span>
            ))}
          </div>
        </div>
      </SectionCard>
    </div>
  );
}
